var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Circle, G, Path, Polygon, Svg } from "react-native-svg";
import { Box, Text } from "native-base";
import { useMemo } from "react";
import { InsulinBodyPart } from "@madmedical/medical";
import { formatInsertPoint } from "../util/insulinFormatters";
import useResponsive from "../util/useResponsive";
const ShowPointData = ({ isReversed = false, sideVisible, bodyPart, leftUnit, rightUnit, }) => {
    const { left, right } = sideVisible;
    return (_jsxs(_Fragment, { children: [!isReversed && (_jsx(Text, Object.assign({ fontSize: "xs", color: "gray.500", mr: "6px" }, { children: formatInsertPoint(bodyPart) }))), _jsxs(_Fragment, { children: [_jsxs(Box, Object.assign({ opacity: left ? 1 : 0, display: "flex", flexDirection: "row", alignItems: "center" }, { children: [_jsx(Box, { bgColor: "blue.500", rounded: "full", w: "6px", h: "6px", mr: "6px" }), _jsx(Text, Object.assign({ fontSize: "xs", color: "blue.600", mr: "6px" }, { children: leftUnit }))] })), _jsxs(Box, Object.assign({ opacity: right ? 1 : 0, display: "flex", flexDirection: "row", alignItems: "center" }, { children: [_jsx(Box, { bgColor: "green.500", rounded: "full", w: "6px", h: "6px", mr: "6px" }), _jsx(Text, Object.assign({ fontSize: "xs", color: "green.600", mr: "6px" }, { children: rightUnit }))] }))] }), isReversed && (_jsx(Text, Object.assign({ fontSize: "xs", color: "gray.500", mx: "6px" }, { children: formatInsertPoint(bodyPart) })))] }));
};
const degToRad = (deg) => deg * (Math.PI / 180.0);
const svgY = (degrees) => degrees + 180;
const calculateEdgePointFn = (center, radius) => (degree, scale = 1) => {
    const degreeInRadians = degToRad(degree);
    const degreeInRadiansY = degToRad(svgY(degree));
    return [
        center + Math.cos(degreeInRadians) * radius * scale,
        center + Math.sin(degreeInRadiansY) * radius * scale,
    ];
};
const bodyPartProperties = {
    [InsulinBodyPart.Waist]: {
        default: {
            left: 150,
            top: -25,
        },
        smallScreen: {
            left: 83,
            top: -20,
        },
        order: 0,
    },
    [InsulinBodyPart.Arm]: {
        order: -1,
        default: {
            left: 355,
            top: 115,
        },
        smallScreen: {
            right: -35,
            top: 59,
        },
    },
    [InsulinBodyPart.Abdomen]: {
        order: -2,
        default: {
            left: 55,
            bottom: 0,
        },
        smallScreen: {
            left: 180,
            bottom: 0,
        },
    },
    [InsulinBodyPart.Glutes]: {
        order: -3,
        default: {
            left: 35,
            bottom: 0,
        },
        smallScreen: {
            left: -10,
            bottom: 0,
        },
    },
    [InsulinBodyPart.Thigh]: {
        order: -4,
        default: {
            left: -80,
            top: 115,
        },
        smallScreen: {
            left: -25,
            top: 59,
        },
    },
};
const defaultBodypartCounts = {
    [InsulinBodyPart.Waist]: 0,
    [InsulinBodyPart.Arm]: 0,
    [InsulinBodyPart.Abdomen]: 0,
    [InsulinBodyPart.Glutes]: 0,
    [InsulinBodyPart.Thigh]: 0,
};
const InsulinAdministrationGraph = ({ bodyChartData, sideVisible, }) => {
    const isEmpty = bodyChartData.length === 0;
    const { isSmallScreen } = useResponsive();
    const { left, right } = bodyChartData.reduce((acc, item) => {
        const { bodyPart, side } = item.insulinBodyLocation;
        acc[side][bodyPart] += 1;
        return acc;
    }, {
        left: Object.assign({}, defaultBodypartCounts),
        right: Object.assign({}, defaultBodypartCounts),
    });
    const MAX_NODE = !isEmpty
        ? Math.max(...Object.values(left), ...Object.values(right))
        : 1;
    const viewBoxSize = isSmallScreen ? 200 : 350;
    const viewBoxCenter = viewBoxSize * 0.5;
    const radius = viewBoxSize * 0.5;
    const BODY_PARTS = 5;
    const SCALE_FACTOR = 1;
    const DISTANCE_UNIT = SCALE_FACTOR / MAX_NODE;
    const grayStroke = "#C0C0C0";
    const greenStroke = "#85B725";
    const blueStroke = "#418DFF";
    const pointRadius = "3.2";
    const calculateEdgePoint = useMemo(() => calculateEdgePointFn(viewBoxCenter, radius), [viewBoxCenter, radius]);
    return (_jsxs(Box, Object.assign({ position: "relative", m: 8 }, { children: [Object.entries(bodyPartProperties).map(([bodyPart, property], i) => {
                const bp = bodyPart;
                const { order } = property, rest = __rest(property, ["order"]);
                const screenProperties = isSmallScreen
                    ? rest.smallScreen
                    : rest.default;
                return (_jsx(Box, Object.assign({ position: "absolute", zIndex: 1, display: "flex", flexDirection: isSmallScreen && bp !== "waist"
                        ? "column"
                        : "row", alignItems: "center" }, screenProperties, { children: _jsx(ShowPointData, { bodyPart: bp, leftUnit: left[bp], rightUnit: right[bp], bodyChartData: bodyChartData, sideVisible: sideVisible }) }), `${bp}-${i * order + i}`));
            }), _jsxs(Svg, Object.assign({ height: viewBoxSize + 10, width: viewBoxSize, viewBox: `0 0 ${viewBoxSize} ${viewBoxSize}` }, { children: [Array.from({ length: BODY_PARTS }).map((_, i) => {
                        const [cx, cy] = calculateEdgePoint(90 + (360 / BODY_PARTS) * i, SCALE_FACTOR);
                        return (_jsxs(G, { children: [_jsx(Path, { fillRule: "evenodd", clipRule: "evenodd", d: `M ${viewBoxCenter} ${viewBoxCenter} ${cx} ${cy} Z`, stroke: grayStroke }, cx + cy + i), Array.from({ length: MAX_NODE !== null && MAX_NODE !== void 0 ? MAX_NODE : 1 }).map((_, j) => {
                                    const [ox, oy] = calculateEdgePoint(90 + (360 / BODY_PARTS) * i, DISTANCE_UNIT * (j + 1));
                                    const [nx, ny] = calculateEdgePoint(90 + (360 / BODY_PARTS) * (i + 1), DISTANCE_UNIT * (j + 1));
                                    return (_jsx(Path, { fillRule: "evenodd", clipRule: "evenodd", d: `M ${ox} ${oy} ${nx} ${ny} Z`, stroke: grayStroke }, nx + ny + i));
                                })] }, i));
                    }), sideVisible.left && (_jsx(Polygon, { stroke: blueStroke, strokeWidth: 2, fill: blueStroke, fillOpacity: 0.15, points: `${Object.entries(left)
                            .map(([bodyPart, units]) => {
                            const edgePoint = calculateEdgePoint(90 +
                                (360 / 5) *
                                    bodyPartProperties[bodyPart].order, units * DISTANCE_UNIT);
                            return `${edgePoint[0]},${edgePoint[1]}`;
                        })
                            .join(" ")}` })), sideVisible.left &&
                        Object.entries(left).map(([bodyPart, units], i) => {
                            const [cx, cy] = calculateEdgePoint(90 +
                                (360 / 5) *
                                    bodyPartProperties[bodyPart].order, units * DISTANCE_UNIT);
                            return (_jsx(Circle, { cx: cx, cy: cy, r: pointRadius, fill: "white", stroke: blueStroke, strokeWidth: 2 }, i));
                        }), sideVisible.right && (_jsx(Polygon, { stroke: greenStroke, strokeWidth: 2, fill: greenStroke, fillOpacity: 0.15, points: `${Object.entries(right)
                            .map(([bodyPart, units]) => {
                            const edgePoint = calculateEdgePoint(90 +
                                (360 / 5) *
                                    bodyPartProperties[bodyPart].order, units * DISTANCE_UNIT);
                            return `${edgePoint[0]},${edgePoint[1]}`;
                        })
                            .join(" ")}` })), sideVisible.right &&
                        Object.entries(right).map(([bodyPart, units], i) => {
                            const [cx, cy] = calculateEdgePoint(90 +
                                (360 / 5) *
                                    bodyPartProperties[bodyPart].order, units * DISTANCE_UNIT);
                            return (_jsx(Circle, { cx: cx, cy: cy, r: pointRadius, fill: "white", stroke: greenStroke, strokeWidth: 2 }, i));
                        })] }))] })));
};
export default InsulinAdministrationGraph;
